import { type Maybe, type ValueEvent } from '@adornis/base/utilTypes.js';
import {
  TimeSlotOverlap,
  isTimeSlotOverlapping,
} from '@adornis/buchungsheld-db/datetime/generate-start-times-for-multiple-overlapping-timeslots.js';
import { Product } from '@adornis/buchungsheld-db/entities/products.js';
import { Workshop } from '@adornis/buchungsheld-db/entities/workshop.js';
import type { BHTranslationDictionary } from '@adornis/buchungsheld-db/translations/translation.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { FormField } from '@adornis/formfield/form-field.js';
import { TranslationController } from '@adornis/translation-core/client/translation-controller.js';
import { html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { combineLatest, filter, map, of, switchMap } from 'rxjs';

@customElement('bh-product-picker')
export class BHProductPicker extends FormField<string[]> {
  @property({ attribute: 'only-workshops', type: Boolean }) isOnlyWorkshops = false;
  @property({ attribute: 'no-overlapping', type: Boolean }) isNoOverlapping = false;
  @property({ attribute: 'placeholder', type: String }) placeholder = '';
  @property({ attribute: 'placeholder-mode', type: String }) placeholderMode = 'static-floating';
  @property({ attribute: false }) institutionID = new RXController<Maybe<string>>(this, undefined);
  @property({ attribute: false }) timeframeID = new RXController<Maybe<string>>(this, undefined);

  @state() private readonly _translation = new TranslationController<BHTranslationDictionary>(this);
  @state() private readonly _products = new RXController(
    this,
    combineLatest([this.institutionID.observable, this.timeframeID.observable]).pipe(
      switchMap(([institutionId, timeframeId]) => {
        if (!institutionId) return of(undefined);
        return Product.subscribeForProductPicker(institutionId, this.isOnlyWorkshops, timeframeId)(Product.allFields);
      }),
      filter(Boolean),
    ),
  );
  @state() private readonly _selectedProducts = new RXController(
    this,
    combineLatest([this.value.observable.pipe(filter(Boolean)), this._products.observable.pipe(filter(Boolean))]).pipe(
      map(([ids, products]) => products.filter(product => ids.includes(product._id))),
    ),
  );

  override render() {
    return html`
      <tr-flex space="md">
        <tr-dropdown-multiselection
          placeholder=${this.placeholder}
          placeholder-mode=${this.placeholderMode}
          .value=${(this._products.value ?? []).filter(product => this.value.value?.includes(product._id))}
          .renderString=${(instance: Product) => instance.name}
          .renderItem=${(instance: Product) => {
            const selectedIndex = this.value.value?.findIndex(id => id === instance._id);
            const isSelected = selectedIndex !== -1;
            const skeleton = (isBlocked: boolean = false) => html`
              <tr-flex
                horizontal
                padding="sm"
                space="sm"
                space-between
                crossaxis-center
                ${css({
                  background: isBlocked ? this.colors.tone.onyx.shade(50) : 'white',
                  cursor: isBlocked ? 'not-allowed' : 'pointer',
                })}
                @click=${e => {
                  e.preventDefault();
                  e.stopPropagation();

                  if (isBlocked && !isSelected) return;
                  if (isSelected) {
                    this.value.value?.splice(selectedIndex, 1);
                  } else {
                    this.value.value?.push(instance._id);
                  }

                  this.value.next(this.value.value);

                  this.requestUpdate();
                }}
              >
                <tr-flex horizontal space="sm" crossaxis-center>
                  <tr-icon big> ${isBlocked ? 'block' : isSelected ? 'check_box' : 'check_box_outline_blank'}</tr-icon>
                  <tr-text>${instance.name}</tr-text>
                </tr-flex>
              </tr-flex>
            `;

            if (instance instanceof Workshop) {
              const isBlocked =
                this._selectedProducts.value?.length === 0
                  ? false
                  : this._selectedProducts.value
                      ?.filter(product => product._id !== instance._id && product instanceof Workshop)
                      .some(product => isTimeSlotOverlapping(product as Workshop, instance) !== TimeSlotOverlap.NONE);
              if (!isBlocked) return skeleton();

              return html` ${skeleton(true)} `;
            }

            return skeleton();
          }}
          .selectables=${this._products.value}
          .emptyState=${this._products.isLoading
            ? html` <tr-text> ${this._translation.translate('loading')} </tr-text> `
            : html` <tr-text> ${this._translation.translate('no_results')} </tr-text> `}
          @value-picked=${(e: ValueEvent<Product[]>) => {
            const products = e.detail.value;
            const ids = products.map(product => product._id);

            this.value.next(ids);
            this.dispatchEvent(new CustomEvent('value-picked', { detail: { value: ids } }));
          }}
        ></tr-dropdown-multiselection>
      </tr-flex>
    `;
  }
}
